import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  promotionApi,
  promotionKeys,
  type Promotion,
  type PromotionInput,
  type PromotionWriteOutcome,
} from '@/entities/promotion';

/**
 * A promotion upsert. On create, `inputs` holds one entry per product × branch
 * pair; on edit, `id` targets the existing promotion and `inputs` holds its
 * single terms replacement.
 */
export interface UpsertPromotionVars {
  id?: Promotion['id'];
  inputs: PromotionInput[];
  /**
   * EDIT only — products the merchant unchecked, to unlink from the campaign.
   *
   * The terms PATCH writes the discount and window and nothing else, so a
   * narrowed product set has to go through `DELETE /promotions/{id}/products`
   * separately. Absent when the selection did not shrink.
   */
  removedProductIds?: string[];
  /** EDIT only — the branch to unlink at (the unlink is per-branch). */
  restaurantId?: string;
}

/**
 * Create or update promotions.
 *
 * NOT optimistic: on create the server mints the promotion id (and the batch
 * endpoint returns ONE id covering every product × branch pair), so a
 * locally-built row would be unaddressable by the edit/delete actions until the
 * refetch landed. The modal closes on success and the list invalidates.
 *
 * A create with several pairs goes through the backend's batch endpoint — one
 * request for the whole cross product, rather than a fan-out of N creates.
 */
export function useUpsertPromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: async ({
      id,
      inputs,
      removedProductIds,
      restaurantId,
    }: UpsertPromotionVars): Promise<PromotionWriteOutcome> => {
      if (id !== undefined) {
        // Unlink first, then patch the terms. Order matters on a gated
        // merchant: both writes queue behind moderation, and unlinking after a
        // terms change would leave the removed products briefly carrying the
        // NEW discount. Detaching first never widens the campaign.
        if (removedProductIds?.length && restaurantId !== undefined) {
          await promotionApi.detachProducts(id, removedProductIds, restaurantId);
        }
        const patch = inputs[0];
        // A promotion narrowed down to nothing but with no terms row left is
        // still a completed edit — the unlink above WAS the change.
        if (!patch) return { status: 'applied', rows: [] };
        return promotionApi.update(id, patch);
      }
      return inputs.length > 1
        ? promotionApi.createBatch(inputs)
        : inputs[0]
          ? promotionApi.create(inputs[0])
          : Promise.resolve({ status: 'applied', rows: [] });
    },
    onSuccess: async () => {
      // `all`, not `list(filters)`: the written promotion may belong to any
      // filtered view, and the key now carries the filters — invalidating one
      // combination would leave every other cached filter stale.
      await queryClient.invalidateQueries({ queryKey: promotionKeys.all });
    },
  });
}
